import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import TextField from '@/components/TextField/TextField';
import { toast } from '@/components/toast';
import { pricingStudioUrl } from '@/environment';
import Back from '@/images/arrows/ArrowLeft.svg';
import InfoCircle from '@/images/circles/InfoCirclePrimary.svg';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import { useCreditQuery } from '@/state/credits/credits';
import theme from '@/theme';
import { ICampaign } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, Divider, InputAdornment } from '@mui/material';
import { useState } from 'react';
import { calculateCampaignCost, CampaignCost } from './CampaignCost';

const AssetsDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        width: 746px;
        padding: 24px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
        max-width: none;
    }
`;

const CloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};

    svg {
        width: 24px;
        height: 24px;
    }
`;

const Title = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TitleText = styled.h2`
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    margin: 0;
    color: ${({ theme }) => theme.colors.secondary.main};
`;

const TitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
`;

const Description = styled.span`
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.15px;
    margin-block-start: 12px;
    max-width: 90%;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;
const CalculatorContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 40px;
    margin-block-start: 32px;
`;

const InnerContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
`;

const InputTitle = styled.span`
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
    color: ${({ theme }) => theme.colors.secondary.main};
`;

const InputSubtitle = styled.span`
    margin-block-start: 4px;
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 15.6px;
    letter-spacing: -0.42px;
`;

const StyledTextField = styled(TextField)`
    & .MuiOutlinedInput-root {
        & fieldset {
        }
        &:hover fieldset {
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
        }
        &.Mui-focused fieldset {
            border: 1px solid ${({ theme }) => theme.colors.secondary.main};
        }
    }

    margin-block-start: 12px;
    width: 100px;
    height: 48px;
    border-radius: 12px;

    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
    input[type='number'] {
        -moz-appearance: textfield;
    }
`;

const BreakdownTitle = styled.span`
    color: ${({ theme }) => theme.colors.black[600]};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 25px;
`;
const BreakdownText = styled.span`
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 20.8px;
    letter-spacing: -0.42px;
`;
const BreakdownRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-block-start: 8px;
    width: 100%;
`;
const StyledDivider = styled(Divider)`
    margin-block: 8px;
    width: 100%;

    &.MuiDivider-root {
        background-color: ${theme.colors.black[300]};
    }
`;

const BreakdownField = ({ title, value }: { title: string; value: string }) => (
    <InnerContainer style={{ width: '100%' }}>
        <BreakdownRow>
            <BreakdownText>{title}</BreakdownText>
            <BreakdownText>{value}</BreakdownText>
        </BreakdownRow>
        <StyledDivider />
    </InnerContainer>
);

const UpgradeBanner = styled.div`
    margin-block-start: 12px;
    display: flex;
    width: 100%;
    padding: 10px 16px;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.primary.bgLight};
`;

const BannerText = styled.span`
    color: ${({ theme }) => theme.colors.black[900]};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
`;
const UpgradeText = styled.a`
    color: ${({ theme }) => theme.colors.primary.main};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.32px;
    &:hover {
        color: ${({ theme }) => theme.colors.primary.hover};
    }
`;

/**
 *
 * @param value A string that represents a number with commas, e.g. "1,000"
 * @returns The number represented by the string, or 0 if the string is empty, e.g. 1000
 */
const toNumber = (value?: string) => {
    if (!value) {
        return 0;
    }

    return parseInt(value.replace(/,/g, '')) || 0;
};

/**
 *
 * @param value A string that represents a number, e.g. "1000"
 * @returns A string that represents the number in a human-readable format, e.g. "1,000"
 */
const numberFormat = (value: string) => {
    const number = toNumber(value);
    return new Intl.NumberFormat().format(number);
};

const DEFAULT_EMAIL_AMOUNT = '10000';
const DEFAULT_CLICK_RATE = 2;

interface Props {
    campaign: ICampaign | null;
    open: boolean;
    onClose: () => void;
    showBackButton?: boolean;
    onClickBack: () => void;
}

export const CreditsCalculatorModal = ({ open, onClose, campaign, showBackButton, onClickBack }: Props) => {
    const [emailAmount, setEmailAmount] = useState<string | undefined>(numberFormat(DEFAULT_EMAIL_AMOUNT));
    const [clickRate, setClickRate] = useState<number | undefined>(DEFAULT_CLICK_RATE);
    const { data: credits, isFetched: isCreditsFetched } = useCreditQuery();
    const mixpanel = useAnalytics();

    const creditsToMinutesEnabled = useFlag('credits_to_minutes_enabled', false);
    const unit = creditsToMinutesEnabled ? 'minutes' : 'credits';

    if (!open || !campaign) {
        return null;
    }

    const cost = calculateCampaignCost({ duration: campaign.duration, emailAmount: toNumber(emailAmount) || 0, clickRate: clickRate || 0 });
    const hasEnoughCredits = isCreditsFetched && !!credits && credits.remaining >= cost;

    return (
        <AssetsDialog open>
            <Container>
                <Title>
                    <TitleContainer>
                        {showBackButton && (
                            <IconButton
                                onClick={() => {
                                    mixpanel?.track('campaigns-calculator', {
                                        event: 'close',
                                        campaign,
                                        num_emails: emailAmount,
                                        open_rate: clickRate,
                                    });
                                    onClickBack();
                                }}
                                ariaLabel="back">
                                <MaskedIcon src={Back} size="24px" />
                            </IconButton>
                        )}
                        <TitleText>Campaign {unit} calculator</TitleText>
                    </TitleContainer>{' '}
                    <CloseButton
                        ariaLabel="close"
                        onClick={() => {
                            mixpanel?.track('campaigns-calculator', {
                                event: 'close',
                                campaign,
                                num_emails: emailAmount,
                                open_rate: clickRate,
                            });
                            onClose();
                        }}>
                        <CloseIcon />
                    </CloseButton>
                </Title>
                <Description>
                    With Video Campaigns, you are charged only for the videos that your users actually watch. To estimate your {unit} costs,
                    please add your campaign projections.
                </Description>

                <CalculatorContainer>
                    <InnerContainer>
                        <InputTitle>How many emails will you send?</InputTitle>
                        <StyledTextField
                            value={emailAmount}
                            onChange={e => setEmailAmount(numberFormat(e.target.value))}
                            placeholder="0"
                            style={{ marginBlockEnd: '28px' }}
                        />

                        <InputTitle>What is your anticipated click rate?</InputTitle>
                        <InputSubtitle>
                            The percentage of recipients who clicked on a call-to-action (CTA) within your email, out of the total number
                            who received it.
                        </InputSubtitle>
                        <StyledTextField
                            type="number"
                            value={clickRate}
                            onChange={e => {
                                const value = parseInt(e.target.value);

                                if (value < 0 || value > 100) {
                                    toast.error({
                                        message: 'Please enter a value between 0 and 100',
                                    });

                                    if (value < 0) {
                                        setClickRate(0);
                                    }
                                    return;
                                }

                                setClickRate(Number(e.target.value) || undefined);
                            }}
                            placeholder="0"
                            InputProps={{
                                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                            }}
                        />
                    </InnerContainer>
                    <InnerContainer
                        style={{
                            borderRadius: '12px',
                            border: `1px solid ${theme.colors.black[300]}`,
                            background: theme.colors.white,
                            padding: '16px 24px',
                            width: '290px',
                            minWidth: '290px',
                        }}>
                        <BreakdownTitle>Breakdown</BreakdownTitle>
                        <BreakdownField title="Est. video duration" value={campaign.duration.toString()} />
                        <BreakdownField title="Expected emails sent" value={emailAmount?.toString() || '0'} />
                        <BreakdownField title="Anticipated click rate" value={`${clickRate?.toString() || 0}%`} />
                        <CampaignCost duration={campaign.duration} emailAmount={toNumber(emailAmount)} clickRate={clickRate || 0} />
                    </InnerContainer>
                </CalculatorContainer>

                {!hasEnoughCredits && (
                    <UpgradeBanner>
                        <MaskedIcon src={InfoCircle} size="32px" color={theme.colors.primary.main} />
                        <BannerText>
                            Looks like you don’t have enough {unit}.{' '}
                            <UpgradeText
                                onClick={() => {
                                    mixpanel?.track('campaigns-calculator', {
                                        event: 'upgrade',
                                        campaign,
                                        num_emails: emailAmount,
                                        open_rate: clickRate,
                                    });
                                    window.open(pricingStudioUrl, '_blank');
                                }}>
                                Upgrade now
                            </UpgradeText>
                        </BannerText>
                    </UpgradeBanner>
                )}
            </Container>
        </AssetsDialog>
    );
};
